import { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { useNavigate } from 'react-router-dom';
import { User, Bell, Shield, Info, UserPlus, Users, Eye, EyeOff, X, Mail, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { roleAPI, RoleDTO } from '@/api/roleAPI';
import { organizationInviteAPI } from '@/api/organizationInviteApi';
 
// ─── Invite User Modal ────────────────────────────────────────────────────────
 
interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}
 
export const InviteUserModal = ({ isOpen, onClose }: InviteUserModalProps) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('');
  const [isInviting, setIsInviting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [roleDetails, setRoleDetails]=useState<RoleDTO[]| null>(null);

  const getRoleDetails = async()=>{
    console.log("yesss---")
    const roleDetails = await roleAPI.getAllRoles();
    console.log("role details ----->", roleDetails)
    setRoleDetails(roleDetails)
  }

  useEffect(()=>{
    getRoleDetails();
  },[])
 
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setRole('');
      setEmailError('');
    }
  }, [isOpen]);
 
  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      setEmailError('Enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

 
  const handleInvite = async () => {
    if (!validateEmail(email)) return;
 
    setIsInviting(true);
    try {
      await organizationInviteAPI.inviteMembersToOrganization({email, role_id:role});
  
      toast.success(`Invitation sent to ${email.trim()}`);
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.detail || 'Failed to send invitation. Please try again.';
      toast.error(message);
    } finally {
      setIsInviting(false);
    }
  };
 
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };
 
  if (!isOpen) return null;
 
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 animate-in fade-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-modal-title"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-primary-600" />
            </div>
            <div>
              <h3 id="invite-modal-title" className="text-lg font-semibold text-gray-900">
                Invite User
              </h3>
              <p className="text-xs text-gray-500">Send an invite link via email</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
 
        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
              type="email"
              value={email}
              onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) validateEmail(e.target.value);
              }}
              onBlur={() => validateEmail(email)}
              className={`input pl-10 ${emailError ? 'border-red-500 focus:ring-red-300' : ''}`}
              placeholder="colleague@company.com"
              disabled={isInviting}
              autoFocus
              />
            </div>
            {emailError && <p className="mt-1.5 text-sm text-red-600">{emailError}</p>}
          </div>
          {roleDetails&& roleDetails.length && <div>
  <label className="block text-sm font-medium text-gray-700 mb-1.5">
    Role <span className="text-red-500">*</span>
  </label>
  <div className="relative">
    <select
      value={role}
      // Assuming 'role' state now stores the ID
      onChange={(e) => setRole(e.target.value)}
      className="input appearance-none pr-10 cursor-pointer"
      disabled={isInviting}
    >
      <option value="" disabled>Select a role</option>
      {roleDetails.map((roleItem) => (
        <option key={roleItem.id} value={roleItem.id}>
          {roleItem.name.charAt(0).toUpperCase() + roleItem.name.slice(1)}
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
  </div>
</div>}
        </div>
 
        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 px-6 pb-6">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={isInviting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleInvite}
            className="btn btn-primary min-w-[110px]"
            disabled={isInviting || !email.trim()}
          >
            {isInviting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Sending…
              </span>
            ) : (
              'Send Invite'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};